<script setup>
import { reactive, ref } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useVuelidate } from '@vuelidate/core';
import { required, helpers } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';
import PageHeader from '../../SettingsSubPageHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const emit = defineEmits(['create']);

const store = useStore();
const router = useRouter();
const { t } = useI18n();

const form = reactive({
  phoneNumber: '',
  sipServer: '',
  sipPort: '5060',
  sipUsername: '',
  sipPassword: '',
  sipTransport: 'udp',
});

const rules = {
  phoneNumber: { required: helpers.withMessage('Phone number is required', required) },
  sipServer: { required: helpers.withMessage('SIP server is required', required) },
};

const v$ = useVuelidate(rules, form);

const isSubmitting = ref(false);

const transportOptions = [
  { value: 'udp', label: 'UDP' },
  { value: 'tcp', label: 'TCP' },
  { value: 'tls', label: 'TLS' },
];

async function createChannel() {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  isSubmitting.value = true;
  try {
    await store.dispatch('inboxes/createChannel', {
      name: `Vobiz Voice (${form.phoneNumber})`,
      channel: {
        type: 'sip',
        phone_number: form.phoneNumber,
        sip_server: form.sipServer,
        sip_port: parseInt(form.sipPort, 10) || 5060,
        sip_username: form.sipUsername || undefined,
        sip_password: form.sipPassword || undefined,
        sip_transport: form.sipTransport,
      },
    });
    router.push({ name: 'settings_inboxes_add_agents' });
  } catch (error) {
    useAlert(error.message);
  } finally {
    isSubmitting.value = false;
  }
}
</script>

<template>
  <form @submit.prevent="createChannel">
    <PageHeader
      :header-title="t('INBOX_MGMT.ADD.VOICE.SIP.TITLE')"
      :header-description="t('INBOX_MGMT.ADD.VOICE.SIP.DESCRIPTION')"
    />
    <div class="grid grid-cols-2 gap-4 p-6">
      <Input
        v-model="form.phoneNumber"
        :label="t('INBOX_MGMT.ADD.VOICE.SIP.PHONE_NUMBER')"
        :placeholder="'+1234567890'"
        :error="v$.phoneNumber.$error"
        :message="v$.phoneNumber.$error ? v$.phoneNumber.$errors[0].$message : ''"
        class="col-span-2"
      />
      <Input
        v-model="form.sipServer"
        :label="t('INBOX_MGMT.ADD.VOICE.SIP.SERVER')"
        :placeholder="'sip.vobiz.com'"
        :error="v$.sipServer.$error"
        :message="v$.sipServer.$error ? v$.sipServer.$errors[0].$message : ''"
        class="col-span-2"
      />
      <div class="col-span-1">
        <label class="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 block">
          {{ t('INBOX_MGMT.ADD.VOICE.SIP.TRANSPORT') }}
        </label>
        <select
          v-model="form.sipTransport"
          class="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm"
        >
          <option
            v-for="opt in transportOptions"
            :key="opt.value"
            :value="opt.value"
          >
            {{ opt.label }}
          </option>
        </select>
      </div>
      <Input
        v-model="form.sipPort"
        :label="t('INBOX_MGMT.ADD.VOICE.SIP.PORT')"
        :placeholder="'5060'"
        type="number"
        class="col-span-1"
      />
      <Input
        v-model="form.sipUsername"
        :label="t('INBOX_MGMT.ADD.VOICE.SIP.USERNAME')"
        :placeholder="'username'"
        class="col-span-1"
      />
      <Input
        v-model="form.sipPassword"
        :label="t('INBOX_MGMT.ADD.VOICE.SIP.PASSWORD')"
        type="password"
        :placeholder="'password'"
        class="col-span-1"
      />
    </div>
    <div class="flex justify-end gap-2 p-6 pt-0">
      <NextButton
        :is-loading="isSubmitting"
        type="submit"
      >
        {{ t('INBOX_MGMT.ADD.VOICE.SIP.CREATE') }}
      </NextButton>
    </div>
  </form>
</template>
